/**
 * Exclude-rules view model.
 *
 * The backend persists `ExcludeRulesTree = { paths, landers, offers }` —
 * one nested rule tree per layer. The authoring UI is friendlier as a
 * FLAT LIST of top-level groups, each carrying its own `appliesTo`
 * layer chip set. This module is the lossless-enough bridge between
 * the two shapes:
 *
 *   ExcludeView  ⟷  ExcludeRulesTree
 *
 * Authoring rules:
 *   - "+ Group" only at the root → max nesting depth = 1 (groups can
 *     contain conditions + NOT-wrapped conditions, but NOT sub-groups).
 *   - Each view group's `appliesTo` lists the layers it fires on.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  ExcludeRulesTree,
  FlowLayer,
  RuleAction,
  RuleNode,
  RuleWeightConfig,
} from '@trafficpulse/shared';
import { parseRuleWeightConfig } from '@trafficpulse/shared';

/**
 * What an ABSENT `actions` field means in stored JSON — the historical exclude
 * behaviour, kept so rules saved before actions existed keep working.
 *
 * NOT the default for a NEW group: those start with no action at all, so the
 * author states what the rule should do rather than inheriting a hidden
 * "exclude". An absent field and an explicitly empty one are different things,
 * which is why the parser tests for the field, not its length.
 */
export const DEFAULT_RULE_ACTIONS: readonly RuleAction[] = ['exclude'];

/** True when `actions` is exactly the default (`['exclude']`) — then we omit it. */
function isDefaultActions(actions: readonly RuleAction[]): boolean {
  return actions.length === 1 && actions[0] === 'exclude';
}

export const ALL_LAYERS: ReadonlyArray<FlowLayer> = ['paths', 'landers', 'offers'];

/** A rule LEAF — a numeric threshold, a score-ranking, or a flow-tag test. */
export type LeafRuleNode = Extract<
  RuleNode,
  { kind: 'condition' | 'ranking' | 'tag' }
>;

/**
 * A view item wraps the raw leaf node rather than spreading its fields, so the
 * three leaf kinds (condition / ranking / tag) round-trip through one path and
 * the editor writes the discriminated node straight back.
 */
export type ExcludeViewItem =
  | { kind: 'leaf'; id: string; node: LeafRuleNode }
  | { kind: 'not'; id: string; child: LeafRuleNode };

/** True for a leaf item (any of the three condition kinds). */
export function isLeafItem(
  item: ExcludeViewItem,
): item is Extract<ExcludeViewItem, { kind: 'leaf' }> {
  return item.kind === 'leaf';
}

/** Top-level rule group. Children are conditions/NOTs only — no nesting. */
export interface ExcludeViewGroup {
  id: string;
  /** Optional label. Blank is normal — the card falls back to "Rule N". */
  name?: string;
  op: 'AND' | 'OR';
  /** Actions applied when this rule matches (default {@link DEFAULT_RULE_ACTIONS}). */
  actions: RuleAction[];
  /** Payload for the `'weight'` action; dropped on serialize without it. */
  weight?: RuleWeightConfig;
  appliesTo: ReadonlyArray<FlowLayer>;
  /** Optional date window the group is evaluated over (unset = layer default). */
  dateRange?: string;
  dateRangeCustomFrom?: string;
  dateRangeCustomTo?: string;
  /** Bank path row ids this rule never applies to. */
  ignoredPathBankIds?: string[];
  children: ExcludeViewItem[];
}

export type ExcludeView = ExcludeViewGroup[];

// ---------------------------------------------------------------------------
// View → ExcludeRulesTree (lossless write path)
// ---------------------------------------------------------------------------

export function viewToTree(view: ExcludeView): ExcludeRulesTree {
  const tree: ExcludeRulesTree = { paths: null, landers: null, offers: null };
  for (const layer of ALL_LAYERS) {
    const inLayer = view.filter((g) => g.appliesTo.includes(layer));
    if (inLayer.length === 0) {
      tree[layer] = null;
    } else if (inLayer.length === 1) {
      tree[layer] = groupToNode(inLayer[0]);
    } else {
      // Multiple groups on the same layer → OR-combine them. The implicit
      // root OR is structural, not user-authored; the UI never shows it.
      tree[layer] = {
        kind: 'group',
        op: 'OR',
        children: inLayer.map(groupToNode),
      };
    }
  }
  return tree;
}

function groupToNode(group: ExcludeViewGroup): RuleNode {
  const name = group.name?.trim();
  return {
    kind: 'group',
    op: group.op,
    // Omitted when blank: an empty string would round-trip as a "named" group
    // whose name renders as nothing.
    ...(name ? { name } : {}),
    // Persist actions only when they diverge from the default, to keep the JSON
    // clean and backward-compatible (absent == ['exclude']).
    ...(isDefaultActions(group.actions) ? {} : { actions: group.actions }),
    ...(group.actions.includes('weight') && group.weight
      ? { weight: group.weight }
      : {}),
    children: group.children.map(itemToNode),
    ...(group.ignoredPathBankIds?.length
      ? { ignoredPathBankIds: group.ignoredPathBankIds }
      : {}),
    ...(group.dateRange
      ? {
          dateRange: group.dateRange,
          dateRangeCustomFrom: group.dateRangeCustomFrom,
          dateRangeCustomTo: group.dateRangeCustomTo,
        }
      : {}),
  };
}

function itemToNode(item: ExcludeViewItem): RuleNode {
  if (item.kind === 'leaf') return item.node;
  return { kind: 'not', child: item.child };
}

/** Only the three authorable leaf kinds survive as a view item. */
function asLeaf(node: RuleNode): LeafRuleNode | null {
  return node.kind === 'condition' || node.kind === 'ranking' || node.kind === 'tag'
    ? node
    : null;
}

// ---------------------------------------------------------------------------
// ExcludeRulesTree → View (best-effort read path)
// ---------------------------------------------------------------------------

/**
 * Deserialise a persisted tree into the view model. Each layer's tree is
 * unfolded into one or more view groups, and groups that appear with
 * identical structure in multiple layers are merged into a single view
 * group with the union of `appliesTo`.
 */
export function treeToView(tree: ExcludeRulesTree, idFn: () => string = uid): ExcludeView {
  const byKey = new Map<string, { group: ExcludeViewGroup }>();
  for (const layer of ALL_LAYERS) {
    const node = tree[layer];
    if (!node) continue;
    const layerGroups = unwrapLayerTree(node);
    for (const sub of layerGroups) {
      const key = canonicalKey(sub);
      const existing = byKey.get(key);
      if (existing) {
        existing.group.appliesTo = uniqueLayers([
          ...existing.group.appliesTo,
          layer,
        ]);
      } else {
        const weight = parseRuleWeightConfig(sub.weight);
        byKey.set(key, {
          group: {
            id: idFn(),
            name: sub.name?.trim() || undefined,
            op: sub.op,
            // Presence, not length: an explicit [] is "no action yet" and must
            // survive a reload, while an absent field is a pre-actions rule.
            actions: sub.actions ? [...sub.actions] : [...DEFAULT_RULE_ACTIONS],
            ...(weight ? { weight } : {}),
            ...(sub.ignoredPathBankIds?.length
              ? { ignoredPathBankIds: [...sub.ignoredPathBankIds] }
              : {}),
            appliesTo: [layer],
            dateRange: sub.dateRange,
            dateRangeCustomFrom: sub.dateRangeCustomFrom,
            dateRangeCustomTo: sub.dateRangeCustomTo,
            children: sub.children.map(nodeToItem).filter(Boolean) as ExcludeViewItem[],
          },
        });
      }
    }
  }
  return Array.from(byKey.values()).map((v) => v.group);
}

/** Strip the implicit OR root our serializer adds when multiple groups live in a layer. */
function unwrapLayerTree(node: RuleNode): Array<Extract<RuleNode, { kind: 'group' }>> {
  if (node.kind === 'group' && node.op === 'OR' && node.children.every((c) => c.kind === 'group')) {
    return node.children as Array<Extract<RuleNode, { kind: 'group' }>>;
  }
  if (node.kind === 'group') return [node];
  // A bare condition or NOT at the top — wrap it into a single AND group so
  // the view stays uniform.
  return [{ kind: 'group', op: 'AND', children: [node] }];
}

function nodeToItem(node: RuleNode): ExcludeViewItem | null {
  const leaf = asLeaf(node);
  if (leaf) return { kind: 'leaf', id: uid(), node: leaf };
  if (node.kind === 'not') {
    const child = asLeaf(node.child); // nested NOT/group not authorable
    return child ? { kind: 'not', id: uid(), child } : null;
  }
  // Nested group at depth >= 2 — flatten by lifting its conditions.
  return null;
}

function canonicalKey(group: Extract<RuleNode, { kind: 'group' }>): string {
  return JSON.stringify({
    op: group.op,
    n: group.name?.trim() || null,
    ac: group.actions ?? DEFAULT_RULE_ACTIONS,
    // Without this, two rules alike except for their weight config would
    // collapse into one on reload and a config would be silently destroyed.
    w: group.weight ?? null,
    dr: group.dateRange ?? null,
    drf: group.dateRangeCustomFrom ?? null,
    drt: group.dateRangeCustomTo ?? null,
    // Sorted: the picker appends, so the same set can arrive in a different
    // order and must still merge. Omitting this entirely is worse — two groups
    // alike except for their ignore lists would collapse and one list would be
    // silently destroyed on the next reload.
    ig: [...(group.ignoredPathBankIds ?? [])].sort(),
    c: group.children.map(canonicalChildKey),
  });
}

function canonicalChildKey(node: RuleNode): unknown {
  // compareTo joins the key: two rules alike except for their comparison
  // would otherwise collapse into one on reload and one would be destroyed —
  // the same hazard the `ig` entry above guards against.
  if (node.kind === 'condition')
    return [
      'c',
      node.metric,
      node.op,
      node.value,
      node.compareTo
        ? [
            node.compareTo.metric,
            node.compareTo.dateRange ?? null,
            node.compareTo.dateRangeCustomFrom ?? null,
            node.compareTo.dateRangeCustomTo ?? null,
            node.compareTo.percent ?? null,
            node.compareTo.against ?? null,
          ]
        : null,
    ];
  if (node.kind === 'ranking')
    return ['r', node.metric, node.op, node.percent ?? null, node.scope ?? null];
  if (node.kind === 'tag') return ['t', node.tagName, node.negate ?? false];
  if (node.kind === 'not') return ['n', canonicalChildKey(node.child)];
  return ['g', node.op, node.children.map(canonicalChildKey)];
}

function uniqueLayers(layers: ReadonlyArray<FlowLayer>): FlowLayer[] {
  return ALL_LAYERS.filter((l) => layers.includes(l));
}

function uid(): string {
  return `gv_${Math.random().toString(36).slice(2, 9)}`;
}

// ---------------------------------------------------------------------------
// View mutation helpers (used by the UI; pure functions for testability)
// ---------------------------------------------------------------------------

export function emptyView(): ExcludeView {
  return [];
}

export function makeGroup(appliesTo: ReadonlyArray<FlowLayer> = ALL_LAYERS): ExcludeViewGroup {
  return {
    id: uid(),
    op: 'AND',
    // Deliberately empty — see DEFAULT_RULE_ACTIONS.
    actions: [],
    ignoredPathBankIds: [],
    appliesTo: [...appliesTo],
    children: [],
  };
}

export function makeCondition(): Extract<ExcludeViewItem, { kind: 'leaf' }> {
  return { kind: 'leaf', id: uid(), node: { kind: 'condition', metric: 'roi', op: '<', value: 0 } };
}

export function makeNot(): Extract<ExcludeViewItem, { kind: 'not' }> {
  return {
    kind: 'not',
    id: uid(),
    child: { kind: 'condition', metric: 'roi', op: '<', value: 0 },
  };
}

export function replaceGroup(
  view: ExcludeView,
  next: ExcludeViewGroup,
): ExcludeView {
  return view.map((g) => (g.id === next.id ? next : g));
}

export function removeGroup(view: ExcludeView, id: string): ExcludeView {
  return view.filter((g) => g.id !== id);
}

/** Bulk-set every group's appliesTo. */
export function setAllLayers(
  view: ExcludeView,
  layers: ReadonlyArray<FlowLayer>,
): ExcludeView {
  return view.map((g) => ({ ...g, appliesTo: [...layers] }));
}
